import React, { useState, useRef, useCallback } from 'react';
import { Volume2, VolumeX, Loader2 } from 'lucide-react';
import { URLService } from '../services/url.service';
import { getAuthHeadersFromStorage } from '../utils/httpUtils';

/**
 * Preview status the parent component can observe to render an inline message
 * (e.g. "Preview not available for this voice" under the voice picker).
 *
 * - 'idle': nothing happening (default before first click, or after stop/reset)
 * - 'loading': fetch+load in flight
 * - 'playing': audio is playing
 * - 'unavailable': server replied 404/503 — provider doesn't ship a sample for this
 *   voice. Generation in production may still work; this is only about the preview button.
 * - 'error': transient failure (timeout, network, unexpected status)
 */
export type VoicePreviewStatus = 'idle' | 'loading' | 'playing' | 'unavailable' | 'error';

interface VoicePreviewButtonProps {
  provider: string;
  voiceId: string;
  language?: string; // Language code for providers that need it (google, pyttsx3)
  disabled?: boolean;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  /** Optional listener for parent UIs to render inline status messages. */
  onStatusChange?: (status: VoicePreviewStatus) => void;
}

/**
 * Button component for previewing TTS voice samples.
 *
 * Voice samples are served from the API endpoint:
 * GET /api/v1/voice-samples/{provider}/{voice_id}/audio
 *
 * The API generates samples on-demand and caches them.
 *
 * Usage:
 * <VoicePreviewButton provider="kokoro" voiceId="af_heart" />
 */

// Singleton: every VoicePreviewButton in the app registers a stop-callback here so we
// can guarantee only one preview plays at a time. Without this, switching provider/voice
// while a sample was still loading caused the OLD audio to start playing AFTER the new
// audio (canplaythrough fires async on the old element) — users heard the previous voice
// regardless of what they had selected.
const activePreviews = new Set<() => void>();
const stopAllPreviewsExcept = (keep?: () => void) => {
  for (const stop of activePreviews) {
    if (stop !== keep) stop();
  }
};

export const VoicePreviewButton: React.FC<VoicePreviewButtonProps> = ({
  provider,
  voiceId,
  language,
  disabled = false,
  className = '',
  size = 'sm',
  onStatusChange,
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  // Distinguishes "this voice has no sample available" from "the preview hit a
  // transient error" (timeout, network glitch). Parent components use this to render
  // the right message.
  const [errorKind, setErrorKind] = useState<'unavailable' | 'error' | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Forward status to parent on every transition. Memoised so listeners only re-render
  // when the derived status actually changes.
  React.useEffect(() => {
    if (!onStatusChange) return;
    let next: VoicePreviewStatus;
    if (isLoading) next = 'loading';
    else if (isPlaying) next = 'playing';
    else if (errorKind === 'unavailable') next = 'unavailable';
    else if (errorKind === 'error' || hasError) next = 'error';
    else next = 'idle';
    onStatusChange(next);
  }, [isLoading, isPlaying, hasError, errorKind, onStatusChange]);
  // Tracks the currently-running fetch+load so we can abort it if the user clicks
  // somewhere else mid-flight. Stale completions used to play the wrong audio.
  const requestIdRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);

  const stopPlayback = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    requestIdRef.current += 1; // invalidate any in-flight fetch
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.oncanplaythrough = null;
      audioRef.current.onended = null;
      audioRef.current.onerror = null;
      if (audioRef.current.src.startsWith('blob:')) {
        URL.revokeObjectURL(audioRef.current.src);
      }
      audioRef.current.removeAttribute('src');
    }
    setIsPlaying(false);
    setIsLoading(false);
  }, []);

  // Register/unregister with the singleton so any other button can ask us to stop.
  React.useEffect(() => {
    activePreviews.add(stopPlayback);
    return () => {
      activePreviews.delete(stopPlayback);
    };
  }, [stopPlayback]);

  // When provider/voiceId/language change, abort any in-flight playback. Otherwise the
  // button could finish loading the previous voice's blob and start playing it under
  // the new label.
  React.useEffect(() => {
    stopPlayback();
    setHasError(false);
    setErrorKind(null);
  }, [provider, voiceId, language, stopPlayback]);

  const getSampleUrl = useCallback(() => {
    const safeVoiceId = voiceId.replace(/[/\\]/g, '_');
    const apiBase = URLService.getAPIBaseUrl();
    const langParam = language ? `?lang=${encodeURIComponent(language)}` : '';
    return `${apiBase}/api/v1/voice-samples/${provider}/${safeVoiceId}/audio${langParam}`;
  }, [provider, voiceId, language]);

  const handlePlay = useCallback(async () => {
    if (disabled) return;

    // Toggle: clicking while playing/loading stops THIS button.
    if (isPlaying || isLoading) {
      stopPlayback();
      return;
    }

    // Stop every other preview button before we start. Without this, two buttons can
    // race on overlapping audio elements and the user hears whichever finished loading
    // last — which is often the older one because pre-baked samples load near-instantly
    // while a fresh on-demand one takes longer.
    stopAllPreviewsExcept(stopPlayback);

    const requestId = ++requestIdRef.current;
    const controller = new AbortController();
    abortRef.current = controller;
    setIsLoading(true);
    setHasError(false);
    setErrorKind(null);

    try {
      const headers = await getAuthHeadersFromStorage();
      // cache: 'no-store' bypasses the browser's HTTP cache. The route still uses
      // ETag/Last-Modified for actual caching, but Electron's persistent cache was
      // serving stale blobs from before the backend bug fixes — which is exactly how
      // "all voices play the last Kokoro voice I clicked" used to manifest.
      const response = await fetch(getSampleUrl(), {
        headers,
        signal: controller.signal,
        cache: 'no-store',
      });

      if (requestId !== requestIdRef.current) return; // user moved on
      if (!response.ok) {
        // 404/503 mean the backend has no sample for this voice. Distinguish from
        // transient errors so the parent can show "Preview not available" instead
        // of a generic retry message.
        const isUnavailable = response.status === 404 || response.status === 503;
        setErrorKind(isUnavailable ? 'unavailable' : 'error');
        throw new Error(`HTTP ${response.status}`);
      }

      const blob = await response.blob();
      if (requestId !== requestIdRef.current) return;

      const blobUrl = URL.createObjectURL(blob);

      if (!audioRef.current) {
        audioRef.current = new Audio();
      }
      const audio = audioRef.current;

      // Drop any handler from a previous request (closures captured stale state).
      audio.oncanplaythrough = null;
      audio.onended = null;
      audio.onerror = null;

      if (audio.src.startsWith('blob:')) {
        URL.revokeObjectURL(audio.src);
      }
      audio.src = blobUrl;

      audio.onended = () => {
        if (requestId !== requestIdRef.current) return;
        setIsPlaying(false);
      };
      audio.onerror = () => {
        if (requestId !== requestIdRef.current) return;
        setHasError(true);
        setIsPlaying(false);
        setIsLoading(false);
      };
      audio.oncanplaythrough = () => {
        // Discard the event if the user clicked another button while we were loading.
        // Without this guard, two preview buttons that fired simultaneously would each
        // resolve their canplaythrough, and the second to fire wins regardless of UI state.
        if (requestId !== requestIdRef.current) {
          audio.pause();
          return;
        }
        setIsLoading(false);
        setIsPlaying(true);
        audio.play().catch(() => {
          setHasError(true);
          setIsPlaying(false);
        });
      };

      audio.load();
    } catch (err) {
      if ((err as Error).name === 'AbortError') return;
      if (requestId !== requestIdRef.current) return;
      setHasError(true);
      setIsLoading(false);
    }
  }, [disabled, isPlaying, isLoading, getSampleUrl, stopPlayback]);

  // Clean up on unmount
  React.useEffect(() => {
    return () => {
      stopPlayback();
      audioRef.current = null;
    };
  }, [stopPlayback]);

  const sizeClasses = {
    sm: 'p-1',
    md: 'p-1.5',
    lg: 'p-2',
  };

  const iconSizes = {
    sm: 'w-3.5 h-3.5',
    md: 'w-4 h-4',
    lg: 'w-5 h-5',
  };

  // Determine button state and styling (dark theme - matches app aurora theme)
  const getButtonStyles = () => {
    if (disabled) {
      return 'bg-black/30 text-white/30 cursor-not-allowed';
    }
    if (errorKind === 'unavailable') {
      // Muted neutral tone — "expected" missing sample. Avoids the alarming red look
      // since the user can still use the voice for actual generation, just not
      // preview it here.
      return 'bg-black/30 text-white/40 cursor-not-allowed';
    }
    if (hasError) {
      return 'bg-red-900/30 text-red-400 hover:bg-red-900/40 cursor-pointer';
    }
    if (isPlaying) {
      return 'bg-purple-900/40 text-purple-300 hover:bg-purple-900/60 cursor-pointer';
    }
    return 'bg-black/30 text-white/70 hover:bg-purple-900/30 hover:text-purple-300 cursor-pointer';
  };

  const getIcon = () => {
    if (isLoading) {
      return <Loader2 className={`${iconSizes[size]} animate-spin`} />;
    }
    if (hasError) {
      return <VolumeX className={iconSizes[size]} />;
    }
    return <Volume2 className={iconSizes[size]} />;
  };

  const getTitle = () => {
    if (disabled) return 'Voice preview unavailable';
    if (errorKind === 'unavailable') return 'Voice sample not available for this voice';
    if (hasError) return 'Sample not available - click to retry';
    if (isPlaying) return 'Click to stop';
    if (isLoading) return 'Loading...';
    return `Preview ${voiceId} voice`;
  };

  return (
    <button
      type="button"
      onClick={handlePlay}
      disabled={disabled}
      title={getTitle()}
      className={`
        rounded-md transition-colors duration-150
        ${sizeClasses[size]}
        ${getButtonStyles()}
        ${className}
      `}
      data-testid={`voice-preview-${provider}-${voiceId}`}
    >
      {getIcon()}
    </button>
  );
};

export default VoicePreviewButton;
